/**
 * Route guards (§11.1) — unauthenticated users never see a shell frame flash,
 * and an authenticated user landing on /login is bounced back in.
 */

import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Skeleton } from "@platform/ui-web";
import { useI18n } from "../i18n/I18nProvider";
import { useAuth } from "./AuthProvider";

/** Branded, calm session-restore state — a skeleton, never a spinner (§14.3). */
function RestoringSession() {
  const { t } = useI18n();
  return (
    <div className="app-restore" role="status" aria-live="polite">
      <div className="app-restore-card">
        <Skeleton height="2.25rem" width="55%" />
        <Skeleton height="1rem" width="80%" />
        <Skeleton height="1rem" width="70%" />
        <span className="app-restore-label">{t("session.restoring")}</span>
      </div>
    </div>
  );
}

export function RequireAuth({ children }: { readonly children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "restoring") return <RestoringSession />;
  if (status === "unauthenticated") {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

export function RedirectIfAuthenticated({ children }: { readonly children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "restoring") return <RestoringSession />;
  if (status === "authenticated") {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? "/"} replace />;
  }
  return <>{children}</>;
}
